import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, RotateCcw, Check, SlidersHorizontal, Info } from 'lucide-react';
import {
  TrackableSymptom,
  getCustomSymptomConfigs,
  saveCustomSymptomConfigs,
  DEFAULT_TRACKABLE_SYMPTOMS,
} from '../lib/symptomCategories';
import { triggerHaptic } from '../lib/haptics';

interface CustomizeSymptomsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string | null;
}

export const CustomizeSymptomsModal: React.FC<CustomizeSymptomsModalProps> = ({
  isOpen,
  onClose,
  userId,
}) => {
  const [symptoms, setSymptoms] = useState<TrackableSymptom[]>([]);
  const [newSymptomName, setNewSymptomName] = useState('');
  const [newSymptomDesc, setNewSymptomDesc] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSymptoms(getCustomSymptomConfigs(userId));
      setIsAdding(false);
      setNewSymptomName('');
      setNewSymptomDesc('');
    }
  }, [isOpen, userId]);

  if (!isOpen) return null;

  const handleToggle = (id: string) => {
    triggerHaptic('light');
    setSymptoms((prev) =>
      prev.map((s) => (s.id === id ? { ...s, enabled: s.enabled === false ? true : false } : s))
    );
  };

  const handleAddCustom = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanName = newSymptomName.trim();
    if (!cleanName) return;

    triggerHaptic('success');
    const slug = cleanName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    const id = `custom_${slug}_${Date.now().toString(36).slice(-4)}`;

    const newSymptom: TrackableSymptom = {
      id,
      name: cleanName,
      shortName: cleanName,
      unit: '/10',
      description: newSymptomDesc.trim() || 'Custom user-tracked symptom',
      enabled: true,
      isCustom: true,
    };

    const updated = [...symptoms, newSymptom];
    setSymptoms(updated);
    saveCustomSymptomConfigs(updated, userId);
    setNewSymptomName('');
    setNewSymptomDesc('');
    setIsAdding(false);
  };

  const handleDeleteCustom = (id: string) => {
    triggerHaptic('medium');
    const updated = symptoms.filter((s) => s.id !== id);
    setSymptoms(updated);
    saveCustomSymptomConfigs(updated, userId);
  };

  const handleResetDefaults = () => {
    triggerHaptic('medium');
    setSymptoms(DEFAULT_TRACKABLE_SYMPTOMS);
    saveCustomSymptomConfigs(DEFAULT_TRACKABLE_SYMPTOMS, userId);
  };

  const handleSaveAndClose = () => {
    triggerHaptic('selection');
    saveCustomSymptomConfigs(symptoms, userId);
    onClose();
  };

  const activeCount = symptoms.filter((s) => s.enabled !== false).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 dark:bg-black/80 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-white dark:bg-[#14161F] w-full max-w-md max-h-[88vh] rounded-3xl p-5 shadow-2xl border border-slate-200/80 dark:border-zinc-800 flex flex-col my-auto text-slate-900 dark:text-zinc-100">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-zinc-800 shrink-0">
          <div className="flex items-center space-x-2">
            <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400">
              <SlidersHorizontal className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-950 dark:text-white">
                Customize Symptom Sliders
              </h2>
              <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500">
                {activeCount} active slider{activeCount !== 1 ? 's' : ''} in logging & charts
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSaveAndClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto py-3 space-y-2 pr-1">
          {/* Instructions banner */}
          <div className="p-3 rounded-2xl bg-slate-50 dark:bg-zinc-900/60 border border-slate-200/80 dark:border-zinc-800/80 flex items-start gap-2.5">
            <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
              Enable the symptoms relevant to you and turn off any you do not experience. You can also add custom symptoms below.
            </p>
          </div>

          {/* List of sliders */}
          <div className="space-y-1.5 pt-1">
            {symptoms.map((s) => {
              const isEnabled = s.enabled !== false;
              return (
                <div
                  key={s.id}
                  className={`p-3 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
                    isEnabled
                      ? 'bg-white dark:bg-zinc-900/90 border-slate-200 dark:border-zinc-800 shadow-2xs'
                      : 'bg-slate-50/50 dark:bg-zinc-950/40 border-slate-200/50 dark:border-zinc-900 text-slate-400 dark:text-zinc-600'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-bold truncate ${
                          isEnabled ? 'text-slate-900 dark:text-zinc-100' : 'text-slate-400 dark:text-zinc-500'
                        }`}
                      >
                        {s.name}
                      </span>
                      {s.isCustom && (
                        <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300">
                          CUSTOM
                        </span>
                      )}
                    </div>
                    {s.description && (
                      <p className="text-[10px] text-slate-500 dark:text-zinc-400 truncate mt-0.5">
                        {s.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {s.isCustom && (
                      <button
                        type="button"
                        onClick={() => handleDeleteCustom(s.id)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                        title="Delete custom symptom"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {/* Toggle pill */}
                    <button
                      type="button"
                      onClick={() => handleToggle(s.id)}
                      className={`w-11 h-6 rounded-full transition-colors p-0.5 flex items-center ${
                        isEnabled ? 'bg-blue-600 dark:bg-blue-500 justify-end' : 'bg-slate-200 dark:bg-zinc-800 justify-start'
                      }`}
                    >
                      <div className="w-5 h-5 rounded-full bg-white shadow-md transform transition-transform" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add custom symptom form */}
          {isAdding ? (
            <form
              onSubmit={handleAddCustom}
              className="p-4 rounded-2xl bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/80 dark:border-blue-900/50 space-y-3 animate-in fade-in duration-150 mt-3"
            >
              <h4 className="text-xs font-bold uppercase tracking-wider text-blue-900 dark:text-blue-200">
                New Custom Symptom Slider
              </h4>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400 mb-1">
                  Symptom Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Brain Fog, Fatigue, Joint Pain"
                  value={newSymptomName}
                  onChange={(e) => setNewSymptomName(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-xs font-semibold text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400 mb-1">
                  Optional Description
                </label>
                <input
                  type="text"
                  placeholder="e.g. Sluggish thinking, heavy eyelids"
                  value={newSymptomDesc}
                  onChange={(e) => setNewSymptomDesc(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-xs text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setIsAdding(false)}
                  className="flex-1 min-h-[38px] rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 text-xs font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 min-h-[38px] rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors shadow-sm"
                >
                  Add Slider
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => {
                triggerHaptic('light');
                setIsAdding(true);
              }}
              className="w-full min-h-[44px] mt-2 rounded-2xl border border-dashed border-slate-300 dark:border-zinc-700 hover:border-blue-500 dark:hover:border-blue-400 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Custom Symptom Slider</span>
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="pt-3 border-t border-slate-100 dark:border-zinc-800 flex items-center justify-between gap-2 shrink-0">
          <button
            type="button"
            onClick={handleResetDefaults}
            className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300 flex items-center gap-1.5 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Reset Defaults</span>
          </button>

          <button
            type="button"
            onClick={handleSaveAndClose}
            className="min-h-[42px] px-5 rounded-xl bg-slate-950 hover:bg-slate-800 dark:bg-white dark:hover:bg-zinc-100 text-white dark:text-slate-950 font-bold text-xs flex items-center gap-1.5 active:scale-95 transition-all shadow-sm"
          >
            <Check className="w-4 h-4" />
            <span>Done</span>
          </button>
        </div>
      </div>
    </div>
  );
};
